import { GiftOutlined } from '@ant-design/icons';
import { App, Empty, Modal, Skeleton } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    AppApc,
    type RedPacketClaimLog,
    type RedPacketCreateLog,
} from '../../api/appApc';
import { getApiErrorMessage } from '../../auth/AuthContext';
import { formatCredits } from '../../data/home';
import { formatDateTime } from '../../i18n/format';
import '../Home/Home.css';

type LogRow =
    | { kind: 'create'; log: RedPacketCreateLog }
    | { kind: 'claim'; log: RedPacketClaimLog };

const LOG_LIMIT = 20;

export async function loadRedPacketLogRows(packetId?: string): Promise<LogRow[]> {
    const claimQuery: { page: number; limit: number; packetId?: string } = {
        page: 1,
        limit: LOG_LIMIT,
    };
    if (packetId) claimQuery.packetId = packetId;

    const [created, claimed] = await Promise.all([
        AppApc.GetRedPacketCreateLogs({
            page: 1,
            limit: LOG_LIMIT,
        }),
        AppApc.GetRedPacketClaimLogs(claimQuery).catch((claimError) => {
            console.error('Error fetching red packet claim logs', claimError);
            return null;
        }),
    ]);
    const createLogs = created.data.data ?? [];
    const claims = claimed?.data.data ?? [];

    return [
        ...createLogs.map((log) => ({ kind: 'create' as const, log })),
        ...claims.map((log) => ({ kind: 'claim' as const, log })),
    ].sort(
        (left, right) =>
            new Date(right.log.createdAt).getTime() - new Date(left.log.createdAt).getTime()
    );
}

function formatWhen(value: string) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return formatDateTime(date, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function formatAmount(amount: string) {
    const value = Number(amount);
    return Number.isFinite(value) ? formatCredits(value) : amount;
}

function eventLabel(event: string, translate: (key: string) => string) {
    return translate(`redPacket.events.${event}`);
}

function messageLabel(log: { event: string; message: string | null }, translate: (key: string) => string) {
    const text = log.message?.trim();
    return text || eventLabel(log.event, translate);
}

function statusTone(event: string) {
    if (event === 'CLAIMED' || event === 'CREDITED' || event === 'CREATED' || event === 'ALLOCATED') {
        return 'success';
    }
    if (event === 'PAYOUT_SENT') return 'processing';
    if (
        event === 'FAILED' ||
        event === 'CREATE_REJECTED' ||
        event === 'CREATE_FAILED' ||
        event === 'REJECTED' ||
        event === 'PAYOUT_FAILED'
    ) {
        return 'danger';
    }
    return 'neutral';
}

function RedPacketLogs({ packetId }: { packetId?: string }) {
    const { t } = useTranslation('activity');
    const { message } = App.useApp();
    const [rows, setRows] = useState<LogRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState<LogRow | null>(null);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            try {
                const next = await loadRedPacketLogRows(packetId);

                if (!cancelled) setRows(next);
            } catch (error) {
                console.error('Error fetching red packet logs', error);
                if (!cancelled) {
                    setRows([]);
                    message.error(getApiErrorMessage(error, t('redPacket.loadFailed')));
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        void load();
        return () => {
            cancelled = true;
        };
    }, [message, packetId, t]);

    if (loading) {
        return (
            <div className="activity-list" aria-busy="true" aria-label={t('redPacket.loading')}>
                {Array.from({ length: 3 }).map((_, index) => (
                    <div className="activity-row activity-row--skeleton" key={index}>
                        <Skeleton.Avatar active size={42} />
                        <span className="activity-main">
                            <Skeleton.Input active size="small" />
                            <Skeleton.Input active size="small" />
                        </span>
                        <Skeleton.Input active size="small" />
                    </div>
                ))}
            </div>
        );
    }

    if (!rows.length) {
        return (
            <div className="activity-empty">
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('redPacket.empty')} />
            </div>
        );
    }

    const selectedLog = selected?.log;
    const detailFields = selectedLog
        ? [
              ['event', eventLabel(selectedLog.event, t)],
              ['amount', formatAmount(selectedLog.amount)],
              ['email', selectedLog.email],
              ['message', selectedLog.message?.trim() || ''],
              ['packetId', selectedLog.packetId || ''],
              ['claimId', selected?.kind === 'claim' ? selected.log.claimId || '' : ''],
              ['payoutTx', selected?.kind === 'claim' ? selected.log.payoutTxHash || '' : ''],
              ['time', formatWhen(selectedLog.createdAt)],
          ].filter(([, value]) => value)
        : [];

    return (
        <>
            <div className="activity-list">
                {rows.map((row) => {
                    const incoming = row.log.event === 'CLAIMED' || row.log.event === 'CREDITED';
                    const outgoing = row.kind === 'create' && row.log.event === 'CREATED';
                    return (
                        <button
                            type="button"
                            className="activity-row"
                            key={`${row.kind}-${row.log.id}`}
                            onClick={() => setSelected(row)}
                        >
                            <span className="brand-icon red-packet">
                                <GiftOutlined />
                            </span>
                            <span className="activity-main">
                                <strong>{eventLabel(row.log.event, t)}</strong>
                                <small>
                                    {formatWhen(row.log.createdAt)}
                                    {row.log.email ? ` · ${row.log.email}` : ''}
                                </small>
                            </span>
                            <span className={`activity-amount${incoming ? ' positive' : ''}`}>
                                {incoming ? '+' : outgoing ? '−' : ''}
                                {formatAmount(row.log.amount)} {t('units.credit', { count: Number(row.log.amount) || 0 })}
                                <small className={`activity-status ${statusTone(row.log.event)}`}>
                                    {messageLabel(row.log, t)}
                                </small>
                            </span>
                        </button>
                    );
                })}
            </div>

            <Modal
                open={Boolean(selected)}
                onCancel={() => setSelected(null)}
                footer={null}
                centered
                className="topup-modal activity-detail-modal"
                title={t('redPacket.detailsTitle')}
            >
                {selectedLog ? (
                    <div className="activity-detail">
                        <div className="activity-detail__summary">
                            <span className={`activity-status ${statusTone(selectedLog.event)}`}>
                                {messageLabel(selectedLog, t)}
                            </span>
                        </div>
                        <div className="activity-detail__list">
                            {detailFields.map(([key, value]) => (
                                <div className="activity-detail__row" key={key}>
                                    <span>{t(`redPacket.fields.${key}`)}</span>
                                    <span className={key === 'packetId' || key === 'claimId' || key === 'payoutTx' ? 'activity-detail__ref' : undefined}>
                                        {value}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : null}
            </Modal>
        </>
    );
}

export default RedPacketLogs;
